import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, Target, Trophy, CheckCircle, MessageSquare,
  LogOut, Calendar, TrendingUp, Star, Send, CalendarPlus,
  BookOpen, Heart, ChevronDown, ChevronUp, Clock, HelpCircle, FileDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const YAS_LOGO = '/yas-logo.png';

// Goal Card Component - Expandable with full details
const GoalCard = ({ goal, onAddToCalendar }) => {
  const [expanded, setExpanded] = useState(false);
  
  const getGoalTypeLabel = (type) => {
    if (type === 'long_term') return { label: 'Long-term', color: 'bg-purple-100 text-purple-800' };
    if (type === 'short_term') return { label: 'Short-term', color: 'bg-blue-100 text-blue-800' };
    return { label: 'Summer Goal', color: 'bg-green-100 text-green-800' };
  };
  
  const goalType = getGoalTypeLabel(goal.goal_type);
  
  return (
    <motion.div 
      layout
      className={`rounded-xl overflow-hidden cursor-pointer transition-all ${
        goal.status === 'completed' ? 'bg-green-50 border border-green-200' : 'bg-gray-50 hover:bg-gray-100'
      }`}
      data-testid={`goal-card-${goal.id}`}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1">
            {goal.status === 'completed' ? (
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            ) : (
              <Target className="w-5 h-5 text-blue-500 flex-shrink-0" />
            )}
            <span className="font-medium">{goal.title}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge className={goalType.color}>{goalType.label}</Badge>
            {goal.status !== 'completed' && goal.deadline && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onAddToCalendar(goal); }}
                className="rounded-lg h-8 px-2"
                title="Add to Google Calendar"
                data-testid={`add-calendar-${goal.id}`}
              >
                <CalendarPlus className="w-4 h-4" />
              </Button>
            )}
            <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </div>
        </div>
        
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-3 pt-3 border-t border-gray-200"
            >
              {/* SMART Goal Details */}
              {goal.specific && (
                <div className="mb-2">
                  <span className="text-xs font-semibold text-gray-500">What:</span>
                  <p className="text-sm text-gray-700">{goal.specific}</p>
                </div>
              )}
              {goal.measurable && (
                <div className="mb-2">
                  <span className="text-xs font-semibold text-gray-500">How I'll measure success:</span>
                  <p className="text-sm text-gray-700">{goal.measurable}</p>
                </div>
              )}
              {goal.achievable && (
                <div className="mb-2">
                  <span className="text-xs font-semibold text-gray-500">How I'll achieve it:</span>
                  <p className="text-sm text-gray-700">{goal.achievable}</p>
                </div>
              )}
              
              {/* Status & Dates */}
              <div className="flex flex-wrap gap-3 mt-3 pt-2 border-t border-gray-100">
                <div className="flex items-center gap-1 text-xs text-gray-600">
                  <Calendar className="w-3 h-3" />
                  <span>Created: {new Date(goal.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                </div>
                {goal.deadline && goal.status !== 'completed' && (
                  <div className="flex items-center gap-1 text-xs text-orange-600 font-medium">
                    <Clock className="w-3 h-3" />
                    <span>Due: {new Date(goal.deadline).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                  </div>
                )}
                {goal.status === 'completed' && goal.completed_at && (
                  <div className="flex items-center gap-1 text-xs text-green-600 font-medium">
                    <CheckCircle className="w-3 h-3" />
                    <span>Completed: {new Date(goal.completed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                  </div>
                )}
              </div>
              
              {/* Status Badge */}
              <div className="mt-2">
                <Badge variant={goal.status === 'completed' ? 'default' : 'outline'} className="capitalize">
                  {goal.status === 'completed' ? '✓ Completed' : goal.status}
                </Badge>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const ParentDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [childData, setChildData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [childJournal, setChildJournal] = useState([]);
  const [showAllJournal, setShowAllJournal] = useState(false);
  const [aiInsights, setAiInsights] = useState(null);

  useEffect(() => {
    fetchChildData();
    fetchParentInsights();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchParentInsights = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ai/parent-insights`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setAiInsights(await res.json());
    } catch (e) { console.error('Parent dashboard error:', e); }
  };

  const fetchChildData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [childRes, messagesRes] = await Promise.all([
        fetch(`${API_URL}/api/parent/child`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/parent/messages`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (childRes.ok) {
        const data = await childRes.json();
        setChildData(data);
        
        // Fetch child's journal entries
        if (data?.child?.id) {
          const journalRes = await fetch(`${API_URL}/api/highlights/youth/${data.child.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (journalRes.ok) {
            setChildJournal(await journalRes.json());
          }
        }
      }
      if (messagesRes.ok) {
        setMessages(await messagesRes.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const getCategoryInfo = (categoryId) => {
    const categories = {
      experience: { label: 'Work Experience', icon: '💼', color: 'bg-blue-500' },
      learned: { label: 'Something Learned', icon: '💡', color: 'bg-yellow-500' },
      challenge: { label: 'Challenge Faced', icon: '💪', color: 'bg-orange-500' },
      achievement: { label: 'Achievement', icon: '🏆', color: 'bg-green-500' },
      grateful: { label: 'Grateful For', icon: '🙏', color: 'bg-purple-500' }
    };
    return categories[categoryId] || categories.experience;
  };

  const formatJournalDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  const sendMessage = async () => {
    if (!message.trim()) {
      toast.error('Please write a message');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/parent/message-monitor?content=${encodeURIComponent(message)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (res.ok) {
        toast.success('Message sent to lead monitor!');
        setShowMessageModal(false);
        setMessage('');
        fetchChildData();
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const addGoalToCalendar = (goal) => {
    const startDate = new Date(goal.deadline);
    const endDate = new Date(goal.deadline);
    endDate.setHours(endDate.getHours() + 1);
    
    const formatDate = (date) => date.toISOString().replace(/-|:|\.\d\d\d/g, '');
    
    const url = new URL('https://calendar.google.com/calendar/render');
    url.searchParams.set('action', 'TEMPLATE');
    url.searchParams.set('text', `🎯 ${child.name}'s Goal: ${goal.title}`);
    url.searchParams.set('details', `SYEP Goal Deadline\n\n${goal.specific || ''}\n\nSet via YAS Connect Parent Dashboard`);
    url.searchParams.set('dates', `${formatDate(startDate)}/${formatDate(endDate)}`);
    
    window.open(url.toString(), '_blank');
    toast.success('Opening Google Calendar...');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-2xl font-heading font-bold text-primary">Loading...</div>
      </div>
    );
  }

  if (!childData?.child) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="p-8 text-center rounded-3xl max-w-md">
          <User className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="font-heading text-xl font-bold mb-2">No Child Linked</h2>
          <p className="text-muted-foreground">
            Your account is not linked to a youth participant yet. Please contact SYEP administration.
          </p>
          <Button onClick={handleLogout} className="mt-4 rounded-full">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </Card>
      </div>
    );
  }

  const { child, goals, accomplishments, recent_checkins, lead_monitor } = childData;

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="p-6 text-white" style={{ background: colors.header_gradient || colors.header }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center gap-4">
              <img src={YAS_LOGO} alt="YAS Connect" className="w-14 h-14 object-contain" />
              <div>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>Parent Dashboard</p>
                <h1 className="font-heading text-2xl font-bold" style={{ color: 'white' }}>Welcome, {user.name}</h1>
              </div>
            </div>
            <Button onClick={handleLogout} variant="ghost" className="text-white hover:bg-white/20 rounded-full">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>

          {/* Child Info Card */}
          <Card className="bg-white/10 backdrop-blur border-white/20 p-4 rounded-2xl">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-4xl">
                {child.avatar_emoji || '😊'}
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-xl font-bold" style={{ color: 'white' }}>{child.name}</h2>
                <p style={{ color: 'rgba(255,255,255,0.9)' }}>{child.work_site || 'Work site not assigned'}</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold" style={{ color: 'white' }}>{child.total_points}</p>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>Points</p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* AI Insights for Parent */}
        {aiInsights && (aiInsights.insights?.length > 0 || aiInsights.suggestions?.length > 0) && (
          <Card className="p-4 rounded-2xl" style={{ background: 'linear-gradient(135deg, #EDE9FE 0%, #FEF3C7 100%)', border: '1px solid #DDD6FE' }}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-violet-500 flex items-center justify-center">
                <Target className="w-4 h-4 text-white" />
              </div>
              <p className="text-sm font-bold text-violet-900">AI Insights for You</p>
            </div>
            {aiInsights.insights?.map((ins, i) => (
              <div key={i} className={`flex items-start gap-2 p-2.5 rounded-xl mb-2 ${
                ins.type === 'celebration' ? 'bg-green-50 border border-green-100' :
                ins.type === 'alert' ? 'bg-amber-50 border border-amber-100' :
                'bg-white/80 border border-violet-100'
              }`}>
                <span className="text-sm flex-shrink-0">{ins.type === 'celebration' ? '🎉' : ins.type === 'alert' ? '⚡' : '💡'}</span>
                <p className="text-xs text-gray-700">{ins.message}</p>
              </div>
            ))}
            {aiInsights.suggestions?.length > 0 && (
              <div className="mt-2 p-3 rounded-xl bg-white/80 border border-violet-100">
                <p className="text-xs font-bold text-violet-800 mb-2">Conversation Starters</p>
                {aiInsights.suggestions.map((s, i) => (
                  <div key={i} className="flex items-start gap-2 mb-1.5">
                    <MessageSquare className="w-3 h-3 text-violet-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-gray-700">{s}</p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-3 rounded-2xl text-center">
            <Star className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
            <p className="text-xl font-bold">{child.current_streak || 0}</p>
            <p className="text-[10px] text-muted-foreground">Day Streak</p>
          </Card>
          <Card className="p-3 rounded-2xl text-center">
            <Target className="w-6 h-6 text-green-500 mx-auto mb-1" />
            <p className="text-xl font-bold">{goals?.length || 0}</p>
            <p className="text-[10px] text-muted-foreground">Goals Set</p>
          </Card>
          <Card className="p-3 rounded-2xl text-center">
            <BookOpen className="w-6 h-6 text-violet-500 mx-auto mb-1" />
            <p className="text-xl font-bold">{childData.courses?.completed || 0}/{13}</p>
            <p className="text-[10px] text-muted-foreground">Courses Done</p>
          </Card>
          <Card className="p-3 rounded-2xl text-center">
            <Trophy className="w-6 h-6 text-orange-500 mx-auto mb-1" />
            <p className="text-xl font-bold">{childData.badges_count || 0}</p>
            <p className="text-[10px] text-muted-foreground">Badges Earned</p>
          </Card>
        </div>

        {/* Child's Interests & Career */}
        {(childData.interests?.length > 0 || childData.careers?.length > 0) && (
          <Card className="p-4 rounded-2xl">
            <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><Heart className="w-4 h-4 text-pink-500" /> About {child.name?.split(' ')[0]}</h3>
            {childData.interests?.length > 0 && (
              <div className="mb-3">
                <p className="text-xs text-gray-500 mb-1.5">Interests</p>
                <div className="flex flex-wrap gap-1.5">
                  {childData.interests.map((int, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-blue-50 text-blue-600 capitalize">{String(int).replace(/_/g, ' ')}</span>
                  ))}
                </div>
              </div>
            )}
            {childData.careers?.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-1.5">Career Interests</p>
                <div className="flex flex-wrap gap-1.5">
                  {childData.careers.map((c, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-orange-50 text-orange-600">{c}</span>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Workshop & Workbook Progress */}
        <Card className="p-4 rounded-2xl">
          <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4 text-violet-500" /> Learning Progress</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl bg-violet-50">
              <p className="text-lg font-bold text-violet-700">{childData.courses?.completed || 0}</p>
              <p className="text-[10px] text-violet-600">Courses Completed</p>
              <p className="text-[9px] text-violet-400">{childData.courses?.in_progress || 0} in progress</p>
            </div>
            <div className="p-3 rounded-xl bg-orange-50">
              <p className="text-lg font-bold text-orange-700">{childData.workbook?.completed || 0}/{childData.workbook?.total || 6}</p>
              <p className="text-[10px] text-orange-600">Workbook Sheets Done</p>
              <p className="text-[9px] text-orange-400">{childData.courses?.total_xp || 0} XP earned</p>
            </div>
          </div>
        </Card>

        {/* Lead Monitor Contact */}
        {lead_monitor && (
          <Card className="p-4 rounded-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Avatar className="w-12 h-12">
                  <AvatarFallback className="bg-primary/10 text-primary font-bold">
                    {lead_monitor.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{lead_monitor.name}</p>
                  <p className="text-sm text-muted-foreground">Lead Monitor</p>
                </div>
              </div>
              <Button onClick={() => setShowMessageModal(true)} className="rounded-full">
                <MessageSquare className="w-4 h-4 mr-2" />
                Message
              </Button>
            </div>
          </Card>
        )}

        {/* Messages Conversation */}
        {messages.length > 0 && (
          <Card className="p-4 rounded-2xl">
            <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-500" />
              Messages with {lead_monitor?.name || 'Lead Monitor'}
            </h3>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {messages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map((msg) => (
                <div 
                  key={msg.id} 
                  className={`flex ${msg.from_role === 'parent' ? 'justify-end' : 'justify-start'}`}
                >
                  <div 
                    className={`max-w-[80%] p-3 rounded-2xl ${
                      msg.from_role === 'parent' 
                        ? 'bg-blue-500 text-white rounded-tr-sm' 
                        : 'bg-gray-100 text-gray-800 rounded-tl-sm'
                    }`}
                  >
                    <p className="text-sm">{msg.content}</p>
                    <p className={`text-xs mt-1 ${msg.from_role === 'parent' ? 'text-blue-200' : 'text-gray-400'}`}>
                      {new Date(msg.created_at).toLocaleDateString('en-US', { 
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                      })}
                      {msg.from_role === 'parent' ? ' • You' : ` • ${msg.from_name || lead_monitor?.name}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <Button 
              onClick={() => setShowMessageModal(true)} 
              variant="outline"
              className="w-full mt-4 rounded-full"
            >
              <Send className="w-4 h-4 mr-2" />
              Send New Message
            </Button>
          </Card>
        )}

        {/* Help & Resources for Parents */}
        <Card className="p-4 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-blue-900">Parent Resources</p>
                <p className="text-xs text-blue-600">Guides and help for using YAS Connect</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => navigate('/parent-about')} variant="outline" className="rounded-xl border-blue-200 text-blue-700 text-xs hover:bg-blue-100">
              About YAS Connect
            </Button>
            <Button onClick={() => navigate('/parent-how-to')} variant="outline" className="rounded-xl border-blue-200 text-blue-700 text-xs hover:bg-blue-100">
              How-To Guide
            </Button>
          </div>
        </Card>

        {/* Download Child's Progress Report */}
        <Card className="p-4 rounded-2xl bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                <FileDown className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-orange-900">Progress Report</p>
                <p className="text-xs text-orange-600">Download {child.name?.split(' ')[0]}&apos;s detailed PDF report</p>
              </div>
            </div>
            <Button 
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-xs"
              data-testid="download-child-pdf"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const res = await fetch(`${API_URL}/api/reports/pdf/youth-detail/${child.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  if (res.ok) {
                    const blob = await res.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${child.name?.replace(/ /g, '_')}_progress_report.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                    toast.success('Report downloaded!');
                  } else {
                    toast.error('Failed to generate report');
                  }
                } catch (e) {
                  toast.error('Connection error');
                }
              }}
            >
              <FileDown className="w-4 h-4 mr-1" /> Download PDF
            </Button>
          </div>
        </Card>

        {/* Goals */}
        <Card className="p-5 rounded-3xl">
          <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-green-500" />
            {child.name}&apos;s Goals
          </h3>
          
          {goals?.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No goals set yet</p>
          ) : (
            <div className="space-y-3">
              {goals?.map((goal) => (
                <GoalCard key={goal.id} goal={goal} onAddToCalendar={addGoalToCalendar} />
              ))}
            </div>
          )}
        </Card>

        {/* Accomplishments */}
        <Card className="p-5 rounded-3xl">
          <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-orange-500" />
            Accomplishments
          </h3>
          
          {accomplishments?.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No accomplishments yet</p>
          ) : (
            <div className="space-y-3">
              {accomplishments?.map((acc) => (
                <div key={acc.id} className="bg-orange-50 rounded-xl p-3">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-orange-500" />
                    <span className="font-medium">{acc.title}</span>
                    <Badge variant="outline" className="ml-auto">{acc.category}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 ml-7">{acc.description}</p>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Work Journal */}
        <Card className="p-5 rounded-3xl">
          <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-500" />
            {child.name}&apos;s Work Journal
            {childJournal.length > 0 && (
              <Badge className="ml-2 bg-blue-100 text-blue-800">{childJournal.length} entries</Badge>
            )}
          </h3>
          
          {childJournal.length === 0 ? (
            <div className="text-center py-6">
              <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No journal entries yet</p>
              <p className="text-sm text-muted-foreground">When {child.name} shares work experiences, they&apos;ll appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {(showAllJournal ? childJournal : childJournal.slice(0, 3)).map((entry) => {
                const category = getCategoryInfo(entry.category);
                return (
                  <div key={entry.id} className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className={`${category.color} text-white`}>
                        {category.icon} {category.label}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{formatJournalDate(entry.created_at)}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-xl">{entry.mood}</span>
                      <p className="text-foreground/80 flex-1">{entry.content}</p>
                    </div>
                    <div className="mt-2 pt-2 border-t flex items-center gap-2 text-xs text-muted-foreground">
                      <Heart className="w-3 h-3" />
                      {entry.likes?.length || 0} likes from group members
                    </div>
                  </div>
                );
              })}
              
              {childJournal.length > 3 && (
                <Button 
                  variant="ghost" 
                  onClick={() => setShowAllJournal(!showAllJournal)}
                  className="w-full text-sm"
                >
                  {showAllJournal ? (
                    <>
                      <ChevronUp className="w-4 h-4 mr-2" />
                      Show Less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4 mr-2" />
                      Show All {childJournal.length} Entries
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </Card>

        {/* Recent Check-ins */}
        <Card className="p-5 rounded-3xl">
          <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-500" />
            Recent Check-ins
          </h3>
          
          {recent_checkins?.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No check-ins yet</p>
          ) : (
            <div className="space-y-4">
              {recent_checkins?.slice(0, 3).map((checkin) => (
                <div key={checkin.id} className="border-l-4 border-blue-500 pl-4 py-2">
                  <div className="flex items-center justify-between mb-2">
                    <Badge>Week {checkin.week_number}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(checkin.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    {checkin.highlight && <p><strong>Highlight:</strong> {checkin.highlight}</p>}
                    {checkin.learned && <p><strong>Learned:</strong> {checkin.learned}</p>}
                    {checkin.mood && <p><strong>Mood:</strong> {checkin.mood}</p>}
                    {checkin.what_learned && <p><strong>Learned:</strong> {checkin.what_learned}</p>}
                    {checkin.most_enjoyed && <p><strong>Enjoyed:</strong> {checkin.most_enjoyed}</p>}
                  </div>
                  {checkin.coach_response && (
                    <div className="bg-green-50 rounded-lg p-2 mt-2">
                      <p className="text-xs font-semibold text-green-700">Coach Response:</p>
                      <p className="text-sm text-green-900">{checkin.coach_response}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Message Modal */}
      <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
        <DialogContent className="sm:max-w-md rounded-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Message to {lead_monitor?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
              className="min-h-[120px] rounded-xl"
            />
            <Button onClick={sendMessage} className="w-full rounded-full">
              <Send className="w-4 h-4 mr-2" />
              Send Message
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParentDashboard;
